/* eslint-disable react-refresh/only-export-components */
import {
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
  createContext
} from "react";
import api from "api/landlord/auth";
import {
  DashboardPayload,
  getCurrentPartner,
  getDashboard
} from "api/landlord/landlord";
import { FEATURES, Partner as PartnerRes } from "types/landlord/partner";
import { useIntercom } from "react-use-intercom";
import { usePostHog } from "posthog-js/react";
import config from "config/config";
import { SupportedPropertyManagementSystem } from "utils/supportedPMS";

export enum SERVICE_TYPE {
  LANDLORD = "landlord",
  FULL_SERVICE = "full_service"
}

export enum ROLE {
  Admin = "admin",
  PropertyManager = "property_manager",
  Owner = "owner"
}

type Address = {
  address1: string;
  address2: string;
  city: string;
  country: string;
  lat: unknown;
  lng: unknown;
  postal_code: string;
  state_or_province: string;
};

type Property = {
  address: Address;
  can_delete: boolean;
  created_at: string;
  id: number;
  name: string;
  number_of_tenants: number;
};

type Authed = {
  id: number;
  email: string;
  name: string;
  partner: Partner;
  role: ROLE;
  can_change_available_by_date: boolean;
  can_change_rent_amount: boolean;
  can_make_decisions: boolean;
  can_view_reports: boolean;
  can_cancel_applications: boolean;
  can_change_unit: boolean;
  can_edit_income: boolean;
  can_override_decision_recommendation: boolean;
  properties: Property[];
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
  };
  stream_chat_token?: string;
  stream_chat_id?: string;
};

type Partner = {
  approved?: boolean;
  key_completed?: boolean;
  status: "active" | "started" | "rejected"; // verify if rejected status is a thing
  billing_completed?: boolean;
  access_key: string;
  email: string;
  name: string;
  properties: Property[];
  service_type: SERVICE_TYPE;
  url: string;
  opt_out_period_days: number;
  opt_in_period_days: number;
  unexpired_opt_in_period: boolean;
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
  };
};

type Auth = undefined | null | Authed;

type Context = {
  auth: Auth;
  partner?: PartnerRes;
  dashboard?: DashboardPayload;
  refreshPartnerData: () => Promise<
    [PromiseSettledResult<void>, PromiseSettledResult<void>]
  >;
  refreshAuth: () => Promise<void>;
  type: {
    isManual: boolean;
    isSDK: boolean;
    isPMS: boolean;
    isBoomScreen: boolean;
    isRentReporting: boolean;

    isFullService: boolean;
    isLandlord: boolean;
    isBoomOps: boolean;
    isAdmin: boolean;
    isPropertyManager: boolean;
    isOwner: boolean;
  };
  permissions: {
    canChangeAvailablebyDate?: boolean;
    canChangeRentAmount?: boolean;
    canMakeDecisions?: boolean;
    canViewReports?: boolean;
    canManageProperties?: boolean;
    canCancelApplications?: boolean;
    canChangeUnit?: boolean;
    canEditIncome?: boolean;
    canOverrideDecisionRecommendation?: boolean;
  };
};

const AppContext = createContext<Context>({
  auth: undefined,
  partner: undefined,
  dashboard: undefined,
  refreshAuth: () => Promise.resolve(),
  refreshPartnerData: () =>
    Promise.allSettled([Promise.resolve(), Promise.resolve()]),
  type: {
    isManual: false,
    isSDK: false,
    isPMS: false,
    isBoomScreen: false,
    isRentReporting: false,

    isFullService: false,
    isLandlord: false,
    isBoomOps: false,
    isAdmin: false,
    isPropertyManager: false,
    isOwner: false
  },
  permissions: {
    canChangeAvailablebyDate: false,
    canChangeRentAmount: false,
    canMakeDecisions: false,
    canViewReports: false,
    canManageProperties: false,
    canChangeUnit: false,
    canEditIncome: true,
    canOverrideDecisionRecommendation: true
  }
});

export const AuthProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  /**
   * Defines if there is an authed user
   * Null - if we could not determine auth (failed request etc.)
   * undefined - if we did not check for auth yet
   * Object - if auth is available!
   */
  const [auth, setAuth] = useState<Auth>(undefined);
  const [partner, setPartner] = useState<PartnerRes>();
  const [dashboard, setDashboard] = useState<DashboardPayload>();

  const posthog = usePostHog();
  const intercom = useIntercom();

  const refreshAuth = () => {
    return (
      api
        .checkAuth()
        // @ts-ignore
        .then((user: any) => {
          return refreshPartnerData().then(() => user);
        })
        .then((user) => {
          setAuth(user);
          posthog.reset();
          intercom.shutdown();
          if (user) {
            posthog.identify(user.id, {
              email: user.email,
              partner_id: user.partner?.id || "no_partner_id",
              partner_name: user.partner?.name || "no_partner_name",
              env: config.environment || "dev"
            });
            intercom.boot({
              name: user.name,
              email: user.email,
              userId: user.intercom_id,
              customAttributes: {
                source: "partner_portal"
              },
              hideDefaultLauncher: true
            });
          } else
            intercom.boot({
              customAttributes: {
                source: "partner_portal"
              }
            });
        })
        .catch(() => {
          posthog.reset();
          intercom.shutdown();
          setAuth(null);
          intercom.boot({
            customAttributes: {
              source: "partner_portal"
            }
          });
        })
    );
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  const refreshPartnerData = () => {
    return Promise.allSettled([
      getCurrentPartner()
        .then((partner) => setPartner(partner.partner))
        .catch(() => {
          // TODO: add error handling
        }),
      getDashboard()
        .then((dashboard) => setDashboard(dashboard))
        .catch(() => {
          // TODO: add error handling
        })
    ]);
  };

  const isSDK = !!partner?.features.includes(FEATURES.SDK),
    isPMS =
      !!partner?.property_management_system?.name &&
      SupportedPropertyManagementSystem.includes(
        partner.property_management_system.name
      ),
    isManual = !isSDK && !isPMS,
    isBoomScreen = !!partner?.features.includes(FEATURES.BOOM_SCREEN),
    isRentReporting = !!partner?.features.includes(FEATURES.RENT_REPORTING),
    isPropertyManager = auth?.role === ROLE.PropertyManager,
    isOwner = auth?.role === ROLE.Owner,
    isAdmin = auth?.role === ROLE.Admin,
    allowPropertyManagementFeature = partner?.features.includes(
      FEATURES.ALLOW_MANUAL_PROPERTY_MANAGEMENT
    );

  return (
    <AppContext.Provider
      value={{
        auth,
        partner,
        refreshAuth,
        dashboard,
        refreshPartnerData,
        permissions: {
          canChangeAvailablebyDate:
            isAdmin || isOwner || Boolean(auth?.can_change_available_by_date),
          canChangeRentAmount:
            isAdmin || isOwner || Boolean(auth?.can_change_rent_amount),
          canMakeDecisions:
            isAdmin || isOwner || Boolean(auth?.can_make_decisions),
          canViewReports: isAdmin || isOwner || Boolean(auth?.can_view_reports),
          canManageProperties:
            (isAdmin || isOwner) && allowPropertyManagementFeature,
          canCancelApplications:
            isAdmin || isOwner || Boolean(auth?.can_cancel_applications),
          canChangeUnit: isAdmin || isOwner || Boolean(auth?.can_change_unit),
          canOverrideDecisionRecommendation:
            isAdmin ||
            isOwner ||
            Boolean(auth?.can_override_decision_recommendation),
          canEditIncome: isAdmin || isOwner || Boolean(auth?.can_edit_income)
        },
        type: {
          isManual,
          isSDK,
          isPMS,
          isBoomScreen,
          isRentReporting,
          isAdmin,
          isPropertyManager,
          isOwner,
          isFullService: partner?.service_type === SERVICE_TYPE.FULL_SERVICE,
          isLandlord: partner?.service_type === SERVICE_TYPE.LANDLORD,
          isBoomOps: Boolean(auth?.email.includes("@boompay.app"))
        }
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export function useAuthContext() {
  return useContext(AppContext);
}
